import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Check, ArrowRight, Sparkles, Zap, Building2, Shield, Info, Crown,
    Users, Briefcase,
} from "lucide-react";
import BookDemoModal from "@/components/BookDemoModal";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ==========================================================================
   Sprint J-2 · Pricing (public marketing page)
   ==========================================================================
   • Dark Nexus One theme (atlas-* tokens; light hex codes are pre-remapped in
     index.css).
   • Catalog-driven: pulls /billing/catalog/public for bundle + alacarte prices
     across three term options.
   • CTA behavior:
       – Test mode + Stripe configured + not logged in → Start free (/signup?plan=SKU)
       – Test mode + Stripe configured + logged in     → Subscribe (/app/billing?plan=SKU)
       – Stripe NOT configured                          → "Coming Soon" (disabled)
       – Enterprise Plus / Enterprise bundle            → Contact sales (BookDemoModal)
       – Platform Owner                                 → "Platform Owner · Lifetime Access" chip
   ========================================================================== */

const TERM_META = {
    monthly: { key: "monthly", label: "Monthly",   badge: null,           short: "/mo" },
    "6month": { key: "6month", label: "6 months",  badge: "Save 30%",     short: "/6mo" },
    annual:  { key: "annual",  label: "Annual",    badge: "Save 35%",     short: "/yr" },
};

const BUNDLE_META = {
    starter:    { pitch: "Solo operators, small teams",       features: ["22-worker catalog", "Standard email support", "Mock integrations sandbox"] },
    pro:        { pitch: "Departments",                       features: ["Priority support (24h)", "Skill Pack Marketplace", "Advanced analytics"], popular: true },
    business:   { pitch: "Mid-market, cross-functional",      features: ["Priority support (4h)", "Audit log", "Workspace analytics"] },
    enterprise: { pitch: "Multi-department orgs",             features: ["SSO (SAML)", "SLA-backed support", "Multi-tenant isolation"] },
    enterprise_plus: { pitch: "Global orgs, verticals, custom SLAs", features: ["Unlimited Workforce Members", "Dedicated implementation", "Custom SLAs & security review", "Named CS partner"] },
};

const ALACARTE_META = {
    junior:    { pitch: "Assistants: Content, Social, SDR",             icon: Users },
    senior:    { pitch: "Specialists: Legal, Finance, HR, Support",     icon: Briefcase },
    executive: { pitch: "C-suite: CFO, COO, Compliance Officer",        icon: Crown },
};

const LEGAL_BUNDLES_FALLBACK = [
    { id: "legal_starter", name: "Legal Starter", price_monthly_usd: 1499, best_for: "Solo practitioners, in-house counsel of small companies", cap_active_matters: 10, features: ["Matter management", "Deadline tracking", "Basic legal document drafting"], included_workers: [], cta: "Start free" },
    { id: "legal_professional", name: "Legal Professional", price_monthly_usd: 3999, best_for: "Mid-market legal teams, boutique law firms", cap_active_matters: 50, features: ["Advanced matter memory", "Deadline auto-extraction", "Discovery workflow", "Ethical walls"], included_workers: [], cta: "Start free" },
    { id: "legal_enterprise", name: "Legal Enterprise", price_display: "Starting at $8,999/mo", best_for: "Enterprises, large law firms, corporate legal teams", cap_active_matters: null, features: ["Unlimited matters", "Custom workflows", "Full audit + eDiscovery", "Dedicated implementation"], included_workers: [], cta: "Contact sales" },
];

const VERTICALS_ROADMAP = [
    { name: "Government Affairs",  phase: "Coming with Phase 5", pitch: "Legislative tracking, advocacy comms, stakeholder mapping" },
    { name: "AI Finance Ops",      phase: "Coming with Phase 5", pitch: "Close acceleration, board-ready reporting, controller stack" },
    { name: "AI Security Ops",     phase: "Coming with Phase 5", pitch: "SOC ops, threat intel, incident response drafting" },
];

const PRICING_FAQ = [
    ["Do you offer annual discounts?",                          "Yes — 30% off when you pay 6 months up-front, 35% off when you pay yearly."],
    ["Is there a free trial?",                                  "Every account starts on a 14-day free trial of the Professional bundle."],
    ["Can I mix workspace bundles and à la carte activations?", "Yes. Your bundle gives you a baseline Workforce Member capacity; à la carte activations stack on top."],
    ["What counts as one Workforce Member?",                    "Each activated Workforce Member — regardless of workload — counts as one member toward your bundle cap."],
    ["What happens if I hit my bundle cap?",                    "You'll see an in-app upgrade prompt. Upgrade the bundle, or add à la carte activations."],
    ["Do Skill Packs cost extra?",                              "Core Employee Skills is free on every activated Workforce Member. Additional packs are one-time or monthly; usage-based packs are metered."],
];

export default function Pricing() {
    const { user } = useAuth();
    const nav = useNavigate();

    const [tab, setTab] = useState("bundles");
    const [term, setTerm] = useState("monthly");
    const [catalog, setCatalog] = useState(null);
    const [legalBundles, setLegalBundles] = useState([]);
    const [skillPacks, setSkillPacks] = useState([]);
    const [demoOpen, setDemoOpen] = useState(false);
    const [demoInterest, setDemoInterest] = useState("");
    const [checkoutBusy, setCheckoutBusy] = useState(null);

    const isOwner = user?.role === "platform_owner";

    useEffect(() => {
        axios.get(`${API_BASE}/billing/catalog`).then((r) => setCatalog(r.data)).catch((e) => {
            console.error("catalog load failed", e);
            toast.error("Pricing catalog failed to load. Please refresh.");
        });
        axios.get(`${API_BASE}/billing/bundles`).then((r) => setLegalBundles(r.data.bundles || [])).catch(() => {});
        axios.get(`${API_BASE}/skill-packs/public`).then((r) => setSkillPacks(r.data.packs || [])).catch(() => {
            const token = localStorage.getItem("wfos_token");
            if (token) {
                axios.get(`${API_BASE}/skill-packs`, { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => setSkillPacks(r.data.packs || [])).catch(() => {});
            }
        });
    }, []);

    const openDemo = (interest) => { setDemoInterest(interest || ""); setDemoOpen(true); };
    const stripeConfigured = catalog?.stripe_configured ?? false;
    // Sprint J-4-E · same LAUNCH_MODE gate as Billing.jsx.
    const launchMode = (process.env.REACT_APP_LAUNCH_MODE || "").toLowerCase();
    const testMode = (catalog?.test_mode ?? false) && launchMode !== "production";

    /** Route a CTA click for a specific SKU based on auth + Stripe state. */
    const handleSubscribe = async (sku) => {
        if (!stripeConfigured) {
            toast.info("Test-mode Stripe wiring pending. Contact sales to reserve your spot.");
            return;
        }
        if (isOwner) {
            toast.info("Platform Owner has lifetime access — no checkout required.");
            return;
        }
        if (!user) {
            nav(`/signup?plan=${encodeURIComponent(sku)}`);
            return;
        }
        setCheckoutBusy(sku);
        try {
            const { data } = await api.post("/billing/checkout", {
                sku, quantity: 1, origin_url: window.location.origin,
                attribution: {
                    source: "pricing_page",
                    referrer: document.referrer || null,
                    tab,
                    term,
                },
            });
            if (data?.url) {
                window.location.assign(data.url);
                return;
            }
            if (data?.coming_soon) {
                toast.info(data.message || "Coming soon.");
            } else if (data?.owner_bypass) {
                toast.info(data.message || "Owner bypass — no checkout needed.");
            } else {
                toast.info("Checkout not available.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed. Please retry.");
        } finally {
            setCheckoutBusy(null);
        }
    };

    return (
        <div className="min-h-screen atlas-hero-bg" data-testid="pricing-page">
            {/* Public nav */}
            <header className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-3 border-b" style={{ borderColor: "var(--atlas-border)" }}>
                <Link to="/" className="inline-flex items-center gap-2 flex-shrink-0">
                    <div className="atlas-logo-mark w-7 h-7 text-[11px]">N</div>
                    <span className="text-[15px] font-semibold tracking-tight" style={{ color: "var(--atlas-text-primary)" }}>Nexus One</span>
                </Link>
                <nav className="flex items-center gap-3 sm:gap-4 text-sm flex-wrap justify-end">
                    <Link to="/" className="hidden sm:inline hover:opacity-90" style={{ color: "var(--atlas-text-tertiary)" }}>Product</Link>
                    <Link to="/pricing" className="hidden sm:inline font-semibold" style={{ color: "var(--atlas-text-primary)" }}>Pricing</Link>
                    <button onClick={() => openDemo("General")} data-testid="nav-book-demo" className="hidden md:inline hover:opacity-90" style={{ color: "var(--atlas-text-tertiary)" }}>Book demo</button>
                    {user ? (
                        <>
                            <Link to="/app/billing" className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-3 sm:px-3.5 h-9 text-[13px] font-medium" data-testid="nav-my-billing">
                                <span className="hidden sm:inline">My billing</span>
                                <span className="sm:hidden">Billing</span>
                            </Link>
                            <Link to="/app" className="atlas-btn-primary inline-flex items-center gap-1 rounded-lg px-3 sm:px-3.5 h-9 text-[13px]" data-testid="nav-open-app">
                                <span className="hidden sm:inline">Open workspace</span>
                                <span className="sm:hidden">Open</span>
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="hover:opacity-90" style={{ color: "var(--atlas-text-tertiary)" }}>Sign in</Link>
                            <Link to="/signup" className="atlas-btn-primary inline-flex items-center gap-1 rounded-lg px-3 sm:px-3.5 h-9 text-[13px]" data-testid="nav-start-free">Start free</Link>
                        </>
                    )}
                </nav>
            </header>

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-6 pt-16 pb-6 text-center relative">
                <div className="atlas-hero-eyebrow" data-testid="pricing-eyebrow">
                    <span className="atlas-section-dot" style={{ marginRight: 0 }} /> Pricing · Sprint J-2
                </div>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" style={{ color: "var(--atlas-text-primary)" }}>
                    Simple pricing.{" "}
                    <span className="atlas-text-gradient">Serious Digital Workforce.</span>
                </h1>
                <p className="mt-5 text-lg max-w-2xl mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Pick a workspace bundle for baseline capacity, activate specialists à la carte, or install Skill Packs to level up your team.
                </p>

                {/* Sprint Q-1 · language reach chip */}
                <div className="mt-4 flex justify-center">
                    <div
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px]"
                        style={{
                            background: "rgba(139,92,246,0.10)",
                            border: "1px solid rgba(139,92,246,0.35)",
                            color: "var(--atlas-primary-hover)",
                        }}
                        data-testid="lang-100-marketing-chip"
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--atlas-primary)", boxShadow: "0 0 8px var(--atlas-primary-glow)" }} />
                        Workforce Members speak 100+ languages
                    </div>
                </div>

                <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-2">
                    {testMode && (
                        <span className="atlas-pill atlas-pill--warning" data-testid="pricing-test-mode-pill">
                            <Sparkles className="w-3 h-3" strokeWidth={2} /> Test mode
                        </span>
                    )}
                    {stripeConfigured ? (
                        <span className="atlas-pill atlas-pill--success" data-testid="pricing-stripe-status">
                            <Check className="w-3 h-3" strokeWidth={2.5} /> Stripe wired
                        </span>
                    ) : (
                        <span className="atlas-pill atlas-pill--warning" data-testid="pricing-stripe-status">
                            <Info className="w-3 h-3" strokeWidth={2} /> Stripe wiring pending
                        </span>
                    )}
                    {isOwner && (
                        <span className="atlas-pill atlas-pill--primary" data-testid="pricing-owner-chip">
                            <Crown className="w-3 h-3" strokeWidth={2} /> Platform Owner · Lifetime Access
                        </span>
                    )}
                </div>
            </section>

            {/* Term + tab controls */}
            <section className="max-w-6xl mx-auto px-6">
                {/* Term toggle */}
                <TermToggle term={term} setTerm={setTerm} />
                {/* Pillar tabs */}
                <div className="mt-5 flex flex-wrap gap-1 rounded-xl p-1 max-w-xl mx-auto"
                     style={{ background: "var(--atlas-bg-elevated)", border: "1px solid var(--atlas-border-strong)" }}
                     data-testid="pricing-tabs">
                    {[
                        { k: "bundles",    label: "Workspace bundles",         Icon: Building2 },
                        { k: "alacarte",   label: "Workforce Members à la carte", Icon: Sparkles },
                        { k: "skillpacks", label: "Skill Packs",               Icon: Zap },
                    ].map(({ k, label, Icon }) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            data-testid={`pricing-tab-${k}`}
                            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-10 rounded-lg text-sm font-medium transition-colors`}
                            style={tab === k ? {
                                background: "var(--atlas-gradient-btn)",
                                color: "#fff",
                                boxShadow: "var(--atlas-shadow-btn)",
                            } : {
                                color: "var(--atlas-text-tertiary)",
                                background: "transparent",
                            }}
                        >
                            <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Panel */}
            <section className="max-w-6xl mx-auto px-6 mt-8 pb-16">
                {tab === "bundles" && (
                    <BundlesPanel
                        catalog={catalog} term={term}
                        stripeConfigured={stripeConfigured}
                        isOwner={isOwner}
                        onSubscribe={handleSubscribe}
                        onContactSales={(interest) => openDemo(interest)}
                        busy={checkoutBusy}
                    />
                )}
                {tab === "alacarte" && (
                    <AlacartePanel
                        catalog={catalog} term={term}
                        stripeConfigured={stripeConfigured}
                        isOwner={isOwner}
                        onSubscribe={handleSubscribe}
                        busy={checkoutBusy}
                    />
                )}
                {tab === "skillpacks" && <SkillPackPanel packs={skillPacks} />}
            </section>

            {/* Legal Department bundles — unchanged */}
            <section className="border-t" style={{ borderColor: "var(--atlas-border)" }} data-testid="legal-bundles-section">
                <div className="max-w-6xl mx-auto px-6 py-16">
                    <div className="text-center">
                        <div className="atlas-label" data-testid="legal-bundles-label">Legal Department bundles</div>
                        <h2 className="mt-3 text-3xl font-bold tracking-tight" style={{ color: "var(--atlas-text-primary)" }}>
                            18 legal AI specialists. One bundle.
                        </h2>
                        <p className="mt-3 text-[15px] max-w-2xl mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Matter management, ethical walls, deadline tracking, and attorney-supervised drafting — bundled by team size.
                        </p>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {(legalBundles.length ? legalBundles : LEGAL_BUNDLES_FALLBACK).map((b) => (
                            <LegalBundleCard key={b.id} bundle={b} onDemo={openDemo} />
                        ))}
                    </div>
                    <div className="mt-6 text-center text-[12.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                        All Legal bundles include the <b>DRAFT — PENDING ATTORNEY REVIEW</b> watermark, ethical walls, attorney approval gates, and matter-scoped audit logs.
                    </div>
                </div>
            </section>

            {/* Verticals roadmap */}
            <section className="border-t" style={{ borderColor: "var(--atlas-border)" }} data-testid="verticals-coming-soon">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    <div className="text-center">
                        <div className="atlas-label">Verticals · roadmap</div>
                        <h3 className="mt-2 text-xl font-semibold" style={{ color: "var(--atlas-text-primary)" }}>More departments launching in 2026</h3>
                    </div>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {VERTICALS_ROADMAP.map((v) => (
                            <div key={v.name} className="atlas-card p-4">
                                <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#fbbf24" }}>{v.phase}</div>
                                <div className="mt-1 text-[15px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>{v.name}</div>
                                <div className="mt-1 text-[13px]" style={{ color: "var(--atlas-text-tertiary)" }}>{v.pitch}</div>
                                <button
                                    onClick={() => openDemo(v.name)}
                                    className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-medium"
                                    style={{ color: "var(--atlas-primary-hover)" }}
                                >
                                    Join waitlist <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Enterprise band */}
            <section data-testid="enterprise-band" style={{ background: "var(--atlas-bg-deep)" }} className="border-t" >
                <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                    <div className="atlas-hero-eyebrow">
                        <Shield className="w-3 h-3" strokeWidth={2} /> Enterprise
                    </div>
                    <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight leading-tight" style={{ color: "var(--atlas-text-primary)" }}>
                        Need SSO, custom SLAs, or 100+ Workforce Members?
                    </h2>
                    <p className="mt-2 max-w-xl mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                        Dedicated implementation, security review, named CS partner, custom Skill Packs. Deployed in your cloud on request.
                    </p>
                    <button
                        onClick={() => openDemo("Enterprise inquiry")}
                        data-testid="enterprise-cta"
                        className="atlas-btn-primary mt-6 inline-flex items-center gap-1.5 rounded-lg px-5 h-11 text-sm"
                    >
                        Talk to enterprise sales <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-4xl mx-auto px-6 py-16">
                <div className="text-center mb-10">
                    <div className="atlas-label">FAQ</div>
                    <h2 className="mt-3 text-3xl font-bold tracking-tight" style={{ color: "var(--atlas-text-primary)" }}>Questions we get a lot.</h2>
                </div>
                <div className="space-y-2" data-testid="pricing-faq">
                    {PRICING_FAQ.map(([q, a], i) => (
                        <details key={i} className="atlas-card p-4 group" data-testid={`pricing-faq-${i}`}>
                            <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                                <span className="text-[15px] font-medium" style={{ color: "var(--atlas-text-primary)" }}>{q}</span>
                                <span className="w-6 h-6 rounded-full grid place-items-center transition-transform group-open:rotate-45"
                                      style={{ border: "1px solid var(--atlas-border-strong)", color: "var(--atlas-text-muted)" }}>
                                    +
                                </span>
                            </summary>
                            <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--atlas-text-tertiary)" }}>{a}</p>
                        </details>
                    ))}
                </div>
            </section>

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} defaultInterest={demoInterest} />
        </div>
    );
}


/* -------------------------- Small building blocks -------------------------- */

function TermToggle({ term, setTerm }) {
    return (
        <div className="mt-2 inline-flex items-center rounded-full p-1 mx-auto"
             style={{ background: "var(--atlas-bg-elevated)", border: "1px solid var(--atlas-border-strong)" }}
             data-testid="pricing-term-toggle">
            {["monthly", "6month", "annual"].map((t) => {
                const meta = TERM_META[t];
                const active = t === term;
                return (
                    <button
                        key={t}
                        onClick={() => setTerm(t)}
                        data-testid={`pricing-term-${t}`}
                        className="px-4 h-9 rounded-full text-[13px] font-medium inline-flex items-center gap-2 transition-colors"
                        style={active ? {
                            background: "var(--atlas-gradient-btn)",
                            color: "#fff",
                            boxShadow: "var(--atlas-shadow-btn)",
                        } : {
                            background: "transparent",
                            color: "var(--atlas-text-tertiary)",
                        }}
                    >
                        {meta.label}
                        {meta.badge && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : ""}`}
                                  style={!active ? {
                                      background: "var(--atlas-success-muted)",
                                      color: "#34d399",
                                      border: "1px solid rgba(16,185,129,0.35)",
                                  } : { color: "#fff" }}>
                                {meta.badge}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function CtaButton({ label, disabled, onClick, testid, variant = "primary", href, chip }) {
    if (chip) {
        return (
            <div
                data-testid={testid}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg h-10 text-[13px] font-semibold"
                style={{
                    background: "var(--atlas-primary-muted)",
                    color: "var(--atlas-primary-hover)",
                    border: "1px solid rgba(139,92,246,0.45)",
                }}
            >
                <Crown className="w-3.5 h-3.5" strokeWidth={2} /> {label}
            </div>
        );
    }
    if (href) {
        return (
            <Link
                to={href}
                data-testid={testid}
                className={variant === "ghost" ? "atlas-btn-ghost w-full inline-flex items-center justify-center gap-1 rounded-lg h-10 text-sm font-medium" : "atlas-btn-primary w-full inline-flex items-center justify-center gap-1 rounded-lg h-10 text-sm"}
            >
                {label}
            </Link>
        );
    }
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={variant === "ghost" ? "atlas-btn-ghost w-full inline-flex items-center justify-center gap-1 rounded-lg h-10 text-sm font-medium" : "atlas-btn-primary w-full inline-flex items-center justify-center gap-1 rounded-lg h-10 text-sm"}
        >
            {label}
        </button>
    );
}

function PriceDisplay({ prices, term, seatsLabel }) {
    if (!prices) return <div className="text-3xl font-bold" style={{ color: "var(--atlas-text-primary)" }}>Custom</div>;
    const p = prices[term];
    if (!p) return null;
    return (
        <div>
            <div className="flex items-end gap-1.5">
                <div className="text-3xl font-bold tabular-nums" style={{ color: "var(--atlas-text-primary)" }}>
                    ${p.total_usd.toLocaleString()}
                </div>
                <div className="text-sm pb-1" style={{ color: "var(--atlas-text-muted)" }}>
                    {TERM_META[term].short}
                </div>
            </div>
            {term !== "monthly" && (
                <div className="mt-0.5 text-[11.5px] tabular-nums" style={{ color: "var(--atlas-text-muted)" }}>
                    ≈ ${p.effective_monthly_usd.toLocaleString()}/mo effective
                </div>
            )}
            {seatsLabel && (
                <div className="mt-1 text-[11px] font-medium" style={{ color: "var(--atlas-primary-hover)" }}>{seatsLabel}</div>
            )}
        </div>
    );
}


/* -------------------------- Panels -------------------------- */

function BundlesPanel({ catalog, term, stripeConfigured, isOwner, onSubscribe, onContactSales, busy }) {
    const bundles = catalog?.bundles || [];
    if (!bundles.length) {
        return <div className="atlas-card p-8 text-center text-sm" style={{ color: "var(--atlas-text-tertiary)" }}>Loading bundles…</div>;
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="bundles-grid">
            {bundles.map((b) => {
                const meta = BUNDLE_META[b.tier] || {};
                const isEntPlus = b.tier === "enterprise_plus";
                const isEnt = b.tier === "enterprise";
                const contactOnly = isEntPlus || b.contact_sales;
                const sku = !contactOnly ? b.prices?.[term]?.sku : null;
                const isBusy = busy === sku;

                let ctaLabel, ctaAction, ctaTestid;
                if (isOwner) {
                    ctaLabel = "Platform Owner · Lifetime Access";
                    ctaTestid = `bundle-cta-${b.tier}-owner`;
                } else if (contactOnly) {
                    ctaLabel = "Contact sales";
                    ctaAction = () => onContactSales(`${b.name} plan`);
                    ctaTestid = `bundle-cta-${b.tier}`;
                } else if (!stripeConfigured) {
                    ctaLabel = "Coming Soon";
                    ctaTestid = `bundle-cta-${b.tier}`;
                } else if (isEnt) {
                    ctaLabel = "Talk to sales";
                    ctaAction = () => onContactSales(`${b.name} plan`);
                    ctaTestid = `bundle-cta-${b.tier}`;
                } else {
                    ctaLabel = isBusy ? "Opening checkout…" : "Subscribe";
                    ctaAction = () => onSubscribe(sku);
                    ctaTestid = `bundle-cta-${b.tier}`;
                }

                const cardHi = meta.popular;

                return (
                    <div
                        key={b.tier}
                        data-testid={`bundle-card-${b.tier}`}
                        className={`atlas-card p-5 flex flex-col relative ${cardHi ? "atlas-card--elevated" : ""}`}
                        style={cardHi ? {
                            borderColor: "var(--atlas-primary)",
                            boxShadow: "var(--atlas-shadow-card-hi)",
                        } : undefined}
                    >
                        {cardHi && (
                            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 atlas-pill atlas-pill--primary text-[10px] px-2 py-0.5"
                                  data-testid={`bundle-badge-${b.tier}`}>
                                Most popular
                            </span>
                        )}
                        <div className="text-[13px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>{b.name}</div>
                        <div className="mt-3">
                            <PriceDisplay
                                prices={b.prices}
                                term={term}
                                seatsLabel={b.seats ? `${b.seats} Workforce Members` : (isEntPlus ? "Unlimited Workforce Members" : null)}
                            />
                        </div>
                        <div className="mt-1 text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>{meta.pitch}</div>
                        <ul className="mt-4 space-y-1.5 text-[12.5px] flex-1" style={{ color: "var(--atlas-text-tertiary)" }}>
                            {(meta.features || []).map((f, i) => (
                                <li key={i} className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-5">
                            {isOwner ? (
                                <CtaButton label={ctaLabel} testid={ctaTestid} chip />
                            ) : (
                                <CtaButton
                                    label={ctaLabel}
                                    testid={ctaTestid}
                                    onClick={ctaAction}
                                    disabled={!ctaAction || isBusy}
                                    variant={contactOnly || isEnt ? "ghost" : "primary"}
                                />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function AlacartePanel({ catalog, term, stripeConfigured, isOwner, onSubscribe, busy }) {
    const alacarte = catalog?.alacarte || [];
    if (!alacarte.length) {
        return <div className="atlas-card p-8 text-center text-sm" style={{ color: "var(--atlas-text-tertiary)" }}>Loading options…</div>;
    }
    return (
        <div>
            <div className="atlas-card p-4 mb-5 flex items-start gap-3" style={{ background: "var(--atlas-primary-muted)", borderColor: "rgba(139,92,246,0.4)" }}>
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} style={{ color: "var(--atlas-primary-hover)" }} />
                <div className="text-[13px]" style={{ color: "var(--atlas-text-primary)" }}>
                    <b>Stackable.</b> À la carte Workforce Members activate on top of your workspace bundle — mix and match as your team grows.
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="alacarte-grid">
                {alacarte.map((a) => {
                    const meta = ALACARTE_META[a.tier] || {};
                    const Icon = meta.icon || Sparkles;
                    const sku = a.prices?.[term]?.sku;
                    const isBusy = busy === sku;
                    let ctaLabel, ctaAction, ctaTestid;
                    if (isOwner) {
                        ctaLabel = "Platform Owner · Lifetime Access";
                        ctaTestid = `alacarte-cta-${a.tier}-owner`;
                    } else if (!stripeConfigured) {
                        ctaLabel = "Coming Soon";
                        ctaTestid = `alacarte-cta-${a.tier}`;
                    } else {
                        ctaLabel = isBusy ? "Opening checkout…" : "Activate";
                        ctaAction = () => onSubscribe(sku);
                        ctaTestid = `alacarte-cta-${a.tier}`;
                    }
                    return (
                        <div key={a.tier} className="atlas-card p-5 flex flex-col" data-testid={`alacarte-card-${a.tier}`}>
                            <div className="inline-flex items-center gap-2">
                                <div className="w-9 h-9 rounded-lg grid place-items-center"
                                     style={{ background: "var(--atlas-primary-muted)", border: "1px solid rgba(139,92,246,0.35)" }}>
                                    <Icon className="w-4 h-4" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />
                                </div>
                                <div className="text-[14px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>{a.name}</div>
                            </div>
                            <div className="mt-4">
                                <PriceDisplay prices={a.prices} term={term} />
                            </div>
                            <div className="mt-1 text-[11.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>{meta.pitch}</div>
                            <div className="mt-5">
                                {isOwner ? (
                                    <CtaButton label={ctaLabel} testid={ctaTestid} chip />
                                ) : (
                                    <CtaButton
                                        label={ctaLabel}
                                        testid={ctaTestid}
                                        onClick={ctaAction}
                                        disabled={!ctaAction || isBusy}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SkillPackPanel({ packs }) {
    if (!packs.length) {
        return (
            <div className="atlas-card p-8 text-center text-sm" style={{ color: "var(--atlas-text-tertiary)" }}>
                Skill pack catalog loading — sign in to see full details.
            </div>
        );
    }
    return (
        <div>
            <div className="atlas-card p-4 mb-4 flex items-start gap-3" style={{ background: "var(--atlas-primary-muted)", borderColor: "rgba(139,92,246,0.4)" }}>
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} style={{ color: "var(--atlas-primary-hover)" }} />
                <div className="text-[13px]" style={{ color: "var(--atlas-text-primary)" }}>
                    <b>Core Employee Skills Pack — included free</b> on every activated Workforce Member. Everything below is optional.
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="skill-packs-grid">
                {packs.map((p) => {
                    const usage = p.usage_pricing || null;
                    const monthly = p.price_monthly ?? p.monthly_price ?? null;
                    const oneTime = p.one_time_price ?? null;
                    const priceLabel = usage
                        ? "Metered"
                        : (monthly != null && monthly > 0) ? `$${monthly}/mo`
                        : oneTime ? `$${oneTime} once`
                        : "Free";
                    return (
                        <div key={p.id || p.slug} data-testid={`skill-pack-${p.id || p.slug}`} className="atlas-card p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-[14px] font-semibold truncate" style={{ color: "var(--atlas-text-primary)" }}>{p.name}</div>
                                    <div className="text-[11px] uppercase tracking-widest mt-0.5" style={{ color: "var(--atlas-text-muted)" }}>{p.category || "Skill"}</div>
                                </div>
                                {usage && (
                                    <span className="atlas-pill text-[9px]">
                                        <Info className="w-2.5 h-2.5" strokeWidth={2} /> Usage-based
                                    </span>
                                )}
                            </div>
                            <p className="mt-2 text-[12.5px] leading-relaxed line-clamp-3" style={{ color: "var(--atlas-text-tertiary)" }}>{p.description}</p>
                            <div className="mt-3 text-[15px] font-semibold tabular-nums" style={{ color: "var(--atlas-text-primary)" }}>{priceLabel}</div>
                            {usage && typeof usage === "object" && (
                                <div className="mt-1 text-[10.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                                    ${usage.price_per_unit ?? "?"} per {usage.unit || "call"}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


/* -------------------------- Legal bundle card (unchanged shape) -------------------------- */

function LegalBundleCard({ bundle, onDemo }) {
    const priceLabel = bundle.price_display
        || (bundle.price_monthly_usd ? `$${bundle.price_monthly_usd.toLocaleString()}/mo` : "Custom");
    const included = bundle.included_workers || [];
    const showCount = Math.min(4, included.length);
    const extraCount = included.length - showCount;
    const isEnterprise = bundle.id === "legal_enterprise";
    const highlight = bundle.id === "legal_professional";
    return (
        <div
            className={`atlas-card p-6 flex flex-col ${highlight ? "atlas-card--elevated" : ""}`}
            style={highlight ? { borderColor: "var(--atlas-primary)" } : undefined}
            data-testid={`legal-bundle-${bundle.id}`}
        >
            {highlight && (
                <span className="self-start atlas-pill atlas-pill--primary text-[10px] px-2 py-0.5 mb-2">
                    Most popular
                </span>
            )}
            <div className="text-[16px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>{bundle.name}</div>
            <div className="mt-2 text-[26px] font-semibold tabular-nums leading-none" style={{ color: "var(--atlas-text-primary)" }}>
                {priceLabel}
            </div>
            <div className="mt-1 text-[12.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                Best for: <span style={{ color: "var(--atlas-text-secondary)" }}>{bundle.best_for}</span>
            </div>

            {(included.length > 0) && (
                <div className="mt-4">
                    <div className="text-[10.5px] font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--atlas-text-muted)" }}>
                        {bundle.included_count ?? included.length} legal Workforce Member{(bundle.included_count ?? included.length) === 1 ? "" : "s"}
                    </div>
                    <div className="flex items-center">
                        {included.slice(0, showCount).map((w, i) => (
                            <img
                                key={w.slug || i}
                                src={w.avatar_url}
                                alt={w.name}
                                title={`${w.name} — ${w.role}`}
                                className="w-8 h-8 rounded-full object-cover border-2 -ml-2 first:ml-0 shadow-sm"
                                style={{ borderColor: "var(--atlas-bg-elevated)" }}
                                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                            />
                        ))}
                        {extraCount > 0 && (
                            <span className="ml-2 text-[11px] font-medium" style={{ color: "var(--atlas-text-muted)" }}>+{extraCount} more</span>
                        )}
                    </div>
                </div>
            )}

            <ul className="mt-4 space-y-1.5 text-[13px] flex-1" style={{ color: "var(--atlas-text-secondary)" }}>
                {(bundle.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />
                        <span>{f}</span>
                    </li>
                ))}
                {bundle.cap_active_matters != null && (
                    <li className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />
                        <span>{bundle.cap_active_matters} active matters</span>
                    </li>
                )}
            </ul>

            {isEnterprise ? (
                <Link
                    to="/legal-enterprise-inquiry"
                    data-testid={`legal-bundle-cta-${bundle.id}`}
                    className="atlas-btn-primary mt-5 inline-flex items-center justify-center gap-1.5 rounded-lg h-10 text-sm"
                >
                    Book an Enterprise Call <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </Link>
            ) : (
                <Link
                    to="/signup"
                    data-testid={`legal-bundle-cta-${bundle.id}`}
                    className="atlas-btn-primary mt-5 inline-flex items-center justify-center gap-1.5 rounded-lg h-10 text-sm"
                >
                    {bundle.cta || "Start free"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </Link>
            )}
        </div>
    );
}
